export const formatDate = (value) => {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(d);
};

export const toDateKey = (date = new Date()) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const addDays = (date, amount) => {
  const d = new Date(date);
  d.setDate(d.getDate() + amount);
  return d;
};

export const getDayName = (date) =>
  new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(date);


export function getSlotStartMinutes(timeSlot = "") {
  const match = String(timeSlot).match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!match) return 0;
  let hour = Number(match[1]);
  const minute = Number(match[2]);
  const meridiem = match[3].toUpperCase();
  if (meridiem === "AM" && hour === 12) hour = 0;
  if (meridiem === "PM" && hour !== 12) hour += 12;
  return hour * 60 + minute;
}

export function getDeliveryStartDate(order) {
  if (!order?.deliveryDate) return null;
  const date = new Date(`${order.deliveryDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  date.setMinutes(getSlotStartMinutes(order.timeSlot || order.deliveryTimeSlot));
  return date;
}

export function isOrderModifiable(order, now = new Date()) {
  const start = getDeliveryStartDate(order);
  return Boolean(start && start.getTime() - now.getTime() > 24 * 60 * 60 * 1000);
}

export function modificationLockText(order, now = new Date()) {
  return isOrderModifiable(order, now)
    ? "Modifications are allowed more than 24 hours before delivery."
    : "Locked: changes are allowed only more than 24 hours before delivery.";
}



export function isDatePaused(schedule, date) {
  if (!schedule || !date) return false;

  const key = typeof date === "string" ? date : toDateKey(date);
  const pauseDates = Array.isArray(schedule.pauseDates)
    ? schedule.pauseDates
    : [];

  if (pauseDates.includes(key)) return true;
  if (schedule.lastSkippedDate === key) return true;

  const pauseRanges = Array.isArray(schedule.pauseRanges)
    ? schedule.pauseRanges
    : [];

  if (
    pauseRanges.some(
      (range) =>
        range?.from &&
        range?.until &&
        key >= range.from &&
        key <= range.until,
    )
  ) {
    return true;
  }

  // Backward compatibility for schedules created before pauseRanges was added.
  if (schedule.pauseFrom && key < schedule.pauseFrom) return false;
  if (schedule.pauseUntil && key > schedule.pauseUntil) return false;

  return Boolean(schedule.pauseFrom || schedule.pauseUntil);
}

export function isSchedulePaused(schedule, from = new Date()) {
  if (!schedule) return false;
  if (schedule.status === "paused") return true;
  return isDatePaused(schedule, toDateKey(from));
}

export function nextDeliveryDate(schedule, from = new Date()) {
  if (!schedule || schedule.status === "cancelled") return null;

  const start = schedule.startDate
    ? new Date(`${schedule.startDate}T00:00:00`)
    : new Date(from);

  const cursor = new Date(from);
  cursor.setHours(0, 0, 0, 0);

  // One-time schedules should not reappear after their start date has passed.
  if (schedule.frequency === "One Time") {
    const key = toDateKey(start);
    return start >= cursor && !isDatePaused(schedule, key) ? key : null;
  }

  if (start > cursor) cursor.setTime(start.getTime());

  const days = Array.isArray(schedule.days)
    ? schedule.days.filter(Boolean)
    : [];

  // Monthly means one delivery per calendar month on the selected weekday.
  // The UI requires exactly one weekday for Monthly, so this remains
  // deterministic and does not accidentally behave like a weekly schedule.
  if (schedule.frequency === "Monthly") {
    const selectedDay = days[0];
    if (!selectedDay) return null;

    for (let monthOffset = 0; monthOffset < 24; monthOffset += 1) {
      const monthStart = new Date(cursor.getFullYear(), cursor.getMonth() + monthOffset, 1);
      const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + monthOffset + 1, 0);
      let candidate = new Date(monthStart);

      while (candidate <= monthEnd) {
        if (getDayName(candidate) === selectedDay) break;
        candidate = addDays(candidate, 1);
      }

      if (candidate <= monthEnd) {
        if (candidate < cursor) continue;
        const key = toDateKey(candidate);
        if (!isDatePaused(schedule, key)) return key;
      }
    }
    return null;
  }

  // Keep the existing fallback behaviour for schedules without selected days.
  if (!days.length) {
    const key = toDateKey(cursor);
    return isDatePaused(schedule, key) ? null : key;
  }

  // A pause can cover a week/month, so look far enough ahead to find
  // the next valid recurring delivery after the pause ends.
  for (let i = 0; i < 370; i += 1) {
    const candidate = addDays(cursor, i);
    const key = toDateKey(candidate);

    if (
      days.includes(getDayName(candidate)) &&
      !isDatePaused(schedule, key)
    ) {
      return key;
    }
  }

  return null;
}

export const getProductLabel = (product, products) => {
  const found = products.find((item) => item.id === product?.productId);
  return (
    found?.name ||
    product?.productName ||
    product?.productId ||
    "Unknown product"
  );
};

const ADMIN_EMAILS = ["shubhshri.77@gmail.com"];

export function getAdminEmails() {
  return ADMIN_EMAILS;
}

export function isAdminEmail(email) {
  if (!email) return false;

  const normalizedEmail = email.trim().toLowerCase();

  return getAdminEmails().includes(normalizedEmail);
}

export function isAdminUser(user) {
  return Boolean(user?.email && isAdminEmail(user.email));
}
